/**
 * use503BRedirect
 * ------------------------------------------------------------------
 * Detects whether the current tenant is a 503B outsourcing facility.
 * When a 503B user hits a legacy 503A-shaped route (EMR queue, legacy
 * Inventory, legacy Compounding, generic Reports & Audit, generic
 * Training), we redirect them to the 503B equivalent so they never
 * see 503A patient/prescription concepts.
 *
 * Maps:
 *   /emr          → /503b               (dashboard)
 *   /inventory    → /503b/inventory-ops (bulk warehouse ops)
 *   /compounding  → /503b/ebr           (Electronic Batch Records)
 *   /reports      → /503b/reports       (Focused Inspection hub)
 *   /training     → /503b/training      (LMS)
 *   /audit        → /compliance-center  (audit-chain + sign-off)
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const ROUTE_MAP_503B: Record<string, string> = {
    '/emr': '/503b',
    '/dashboard': '/503b',
    '/inventory': '/503b/inventory-ops',
    '/compounding': '/503b/ebr',
    '/reports': '/503b/reports',
    '/training': '/503b/training',
    '/audit': '/compliance-center',
};

export function use503BRedirect(currentPath: string): { checked: boolean; is503B: boolean } {
    const { token } = useAuth();
    const { tenantSlug } = useTenant();
    const navigate = useNavigate();
    const [checked, setChecked] = useState(false);
    const [is503B, setIs503B] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                if (!backendUrl || !tenantSlug) {
                    if (!cancelled) { setChecked(true); setIs503B(false); }
                    return;
                }
                const res = await fetch(`${backendUrl}/api/503b/tenant-status`, {
                    credentials: 'include',
                    headers: {
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        'X-Tenant-Slug': tenantSlug,
                    },
                });
                const flag = res.ok;
                if (!cancelled) {
                    setIs503B(flag);
                    setChecked(true);
                }
                if (flag && ROUTE_MAP_503B[currentPath]) {
                    navigate(ROUTE_MAP_503B[currentPath], { replace: true });
                }
            } catch {
                if (!cancelled) { setChecked(true); setIs503B(false); }
            }
        };
        check();
        return () => { cancelled = true; };
    }, [token, tenantSlug, currentPath, navigate]);

    return { checked, is503B };
}
